import { useEffect, useRef } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { resolveLanguageFromSiteCode, hasMappedLanguage } from '../i18n/countryLanguage';
import { useLogger } from '../services/telemetry/useLogger';
import type { LanguageCode } from '../../features/Settings/utils/languageUtils';

export type CountryDefaultLanguage = {
  language: LanguageCode | null;
  isSettled: boolean;
};

export const useCountryDefaultLanguage = (): CountryDefaultLanguage => {
  const { isFullyAuthenticated, flags } = useAuth();
  const { siteCode } = useUser();
  const logger = useLogger();
  const loggedPrefix = useRef<string | null>(null);

  const enabled = isFullyAuthenticated && flags?.ENABLE_COUNTRY_DEFAULT_LANGUAGE === true;
  const prefix = siteCode.slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!enabled || siteCode === '' || hasMappedLanguage(prefix)) return;
    if (loggedPrefix.current === prefix) return;
    loggedPrefix.current = prefix;
    logger.info('Site prefix has no mapped default language, defaulting to English', {
      'site.prefix': prefix,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, siteCode, prefix]);

  if (!enabled) return { language: null, isSettled: true };
  if (siteCode === '') return { language: null, isSettled: false };

  return { language: resolveLanguageFromSiteCode(prefix), isSettled: true };
};
